import React, {useState, useMemo, useEffect, useCallback} from "react"
import {IntlProvider, addLocaleData} from "react-intl"
import en from "react-intl/locale-data/en"
import fr from "react-intl/locale-data/fr"

import authentication from "@irati/auth"
import {allSettled} from "../functions"
import request from "./service/request"
import getSupportedLocale, {DEFAULT_LANG} from "./helpers/getSupportedLocale"
import {BucketState, LangLoaderContext, LangLoaderProvider} from "./useLangLoader"

addLocaleData([...en, ...fr])

/**
 * Abstraction over some async storage.
 * For native can pass in AsyncStorage,
 * For web just wrap localStorage methods in async wrappers
 */
type Storage = {
  setItem(key: string, value: any): Promise<void>
  getItem(key: string): Promise<string | null>
  removeItem(key: string): Promise<void>
  getAllKeys(callback?: (error?: Error) => void): Promise<string[]>
}

type Props = {
  /** Storage mechanism to use for persisting phrase keys. This is only intended for
   * use by native because it can push phrase keys into the bundle with any code push.
   * i.e the client bundle won't rely on phrase keys that aren't already in storage
   */
  storage?: Storage
  /** Initial messages to render. If present these will allow the app to render immediately
   * and any new messages will be fetched in the background, overriting old values as they load.
   * This is only intended for native to allow injecting the bundled phrase keys as
   * part of any codepush.
   * This could also be used in local development if the local keys were required in at the provder level
   */
  initialBuckets?: InitialBuckets
  children: React.ReactNode
  /** Only intended for native*/
  textComponent?: IntlProvider.Props["textComponent"]
}

type Messages = Record<string, string>

type InitialBuckets = Map<string, Messages>

type Buckets = Map<
  string,
  {
    state: BucketState
    messages: Messages
  }
>

const KEY = "PerkboxLangStorage"

/**
 * LangProvider is a wrapper around IntlProvider which fetches the latest keys from cms.
 * It supports an async storage mechanism to quickly reload cached keys on restart (intended for the native app).
 * Injected keys are also supported to allow native to bundle keys with the js bundle.
 *
 * If initialBuckets are provided, these will render immidiately without waiting for the remote keys to be fetched.
 *
 * If keys are found in storage, these will override any keys initially set, and then be rendered.
 *
 * Finally when remote keys are loaded, these will override any initial or storage keys. The shared react context ensures
 * all components will recieve the more up-to-date keys as they become available without blocking the app from rendering.
 */
export const LangProvider = React.memo(function LangProviderC({children, textComponent, storage, initialBuckets}: Props) {
  /** at this time we only support a single locale, and it was not tested to work, but the code was kept from the
   * original implementation to make a transition easier in future.
   */
  const [locale, setLocale] = useState(() => getSupportedLocale(authentication.userLocale()) || DEFAULT_LANG)
  /** buckets are stored in a es6 Map in an attempt to aid performance. */
  const [buckets, setBuckets] = useState(() => (initialBuckets ? createInitialState(initialBuckets) : (new Map() as Buckets)))

  useEffect(() => {
    // this is to handle the component unmounting and remounting
    let ignored = false

    if (storage) {
      loadCachedMessages({storage}).then((stored) => {
        if (!ignored && stored) {
          setBuckets((b) => setBucketsFromStorage(b, stored))
        }
      })
    }

    return () => {
      ignored = true
    }
  }, [storage, setBuckets])

  const getContent = useCallback(
    (bucketKeys: string[], newLocale: string) => {
      if (locale !== newLocale) {
        setLocale(newLocale)
      }

      const bucketsToFetch: string[] = []

      bucketKeys.forEach((key) => {
        if (!buckets.has(key) || buckets.get(key)?.state === "local") {
          bucketsToFetch.push(key.toLowerCase())
        }
      })

      if (bucketsToFetch.length == 0) {
        // we have already seen and loaded
        return Promise.resolve()
      }

      setBuckets((b) => {
        // update our Map
        bucketsToFetch.forEach((key) => {
          if (b.has(key)) {
            b.set(key, {messages: b.get(key)!.messages, state: "updating"})
          } else {
            b.set(key, {messages: {}, state: "fetching"})
          }
        })

        return b
      })

      return Promise.all(
        bucketsToFetch.map((bucketName) => request(bucketName, newLocale).then((keys: Messages | undefined) => ({bucketName, keys})))
      ).then((results) => {
        const newBuckets = new Map(buckets)

        results.forEach(({bucketName, keys}) => {
          const original = newBuckets.get(bucketName)
          if (storage && keys) {
            storage.setItem(`${KEY}:${bucketName.toLowerCase()}`, JSON.stringify(keys)).catch(console.error)
          }
          newBuckets.set(bucketName, {
            messages: {
              ...original?.messages,
              ...keys
            },
            state: "latest"
          })
        })

        setBuckets(newBuckets)
      })
    },
    [locale, setLocale, setBuckets, storage, buckets]
  )

  const messages = useMemo(() => [...buckets.values()].reduce((msgs: Messages, b) => ({...msgs, ...b.messages}), {}), [buckets])

  const loaderCtx = useMemo((): LangLoaderContext => {
    return {
      getContent,
      bucketStates: [...buckets.entries()].reduce(
        (states: LangLoaderContext["bucketStates"], [key, bucket]) => ({
          ...states,
          [key]: bucket.state
        }),
        {}
      )
    }
  }, [buckets, getContent])

  return (
    <LangLoaderProvider value={loaderCtx}>
      <IntlProvider
        locale={locale}
        textComponent={textComponent}
        key={locale}
        messages={messages}
        onError={(error) => {
          console.log(error)
        }}>
        {children}
      </IntlProvider>
    </LangLoaderProvider>
  )
})

async function loadCachedMessages({storage}: {storage: Storage}) {
  const keys = await storage.getAllKeys().catch(console.error)

  if (!keys) return

  const results = await allSettled(
    keys.filter((key) => key.startsWith(KEY)).map(async (key) => [key.replace(`${KEY}:`, ""), await storage.getItem(key)] as const)
  )

  if (results.length === 0) return

  return results.reduce((storedMessages, res) => {
    if (res.status === "rejected" || !res.value?.[1]) {
      return storedMessages
    }

    const [key, messages] = res.value

    try {
      const phraseKeys = JSON.parse(messages)
      storedMessages.set(key, phraseKeys)
      return storedMessages
    } catch (e) {
      console.warn(e)
      return storedMessages
    }
  }, new Map<string, Messages>())
}

function setBucketsFromStorage(buckets: Buckets, storedMessages: Map<string, Messages>) {
  const keys: string[] = []

  Array.from(storedMessages.keys()).forEach((key) => {
    // so long as we havn't already fetched the latest keys, we want to use them
    if ("latest" !== buckets.get(key)?.state) {
      keys.push(key)
    }
  })

  if (keys.length === 0) return buckets

  // update our Map
  keys.forEach((key) => buckets.set(key, {messages: storedMessages.get(key)!, state: "local"}))

  // and then assign it to a new Map so React sees them as different and re-renders
  return new Map(buckets)
}

function createInitialState(initialBuckets: InitialBuckets): Buckets {
  return new Map([...initialBuckets.entries()].map(([name, keys]) => [name.toLowerCase(), {messages: keys, state: "local" as const}]))
}
